import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Settings, X, Factory, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

interface Production {
  id: number;
  machineId: number;
  beamId: number;
  qualityId: number;
  employeeId: number;
  meters: number;
  metersProduced: number;
  errorRate: number;
  status: 'active' | 'completed' | 'pending';
  tenantId: number | null;
  createdAt: string;
  machine: {
    id: number;
    number: string;
  };
  beam: {
    id: number;
    number: string;
    totalMeters: number;
  };
  quality: {
    id: number;
    name: string;
  };
  employee: {
    id: number;
    user: {
      name: string;
    };
  };
}

interface Machine {
  id: number;
  number: string;
  isActive: boolean;
}

interface Beam {
  id: number;
  number: string;
  totalMeters: number;
  quality?: {
    id: number;
    name: string;
  };
}

interface Employee {
  id: number;
  user: {
    name: string;
  };
}

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
}

interface ProductionForm {
  machineId: number;
  beamId: number;
  qualityId: number;
  employeeId: number;
  meters: number;
  errorRate?: number;
  tenantId?: number | null;
}

const Production: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduction, setEditingProduction] = useState<Production | null>(null);
  const [deletingProduction, setDeletingProduction] = useState<Production | null>(null);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { scope } = useSuperAdminCompany();
  const showCompanyColumn = user?.role === 'SUPERADMIN' && scope.mode === 'all';
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductionForm>();

  // Fetch productions
  const { data: productionsResponse, isLoading, error } = useQuery({
    queryKey: ['productions'],
    queryFn: () => apiService.get('/production'),
  });
  
  const productions = productionsResponse?.productions || [];

  // Fetch machines
  const { data: machinesResponse } = useQuery({
    queryKey: ['machines'],
    queryFn: () => apiService.get('/machines'),
  });
  
  const machines = machinesResponse?.machines || [];

  // Fetch beams
  const { data: beamsResponse } = useQuery({
    queryKey: ['beams'],
    queryFn: () => apiService.get('/beams'),
  });
  
  const beams = beamsResponse?.beams || [];

  // Fetch employees
  const { data: employeesResponse } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
  });
  
  const employees = employeesResponse?.employees || [];

  // Fetch qualities
  const { data: qualitiesResponse } = useQuery({
    queryKey: ['qualities'],
    queryFn: () => apiService.get('/qualities'),
  });
  
  const qualities = qualitiesResponse?.qualities?.filter((q: Quality) => q.isActive) || [];

  // Create production mutation
  const createMutation = useMutation({
    mutationFn: (data: ProductionForm) => apiService.post('/production', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      toast.success('Production record created successfully');
      setIsModalOpen(false);
      setSelectedTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create production record');
    },
  });

  // Update production mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProductionForm }) =>
      apiService.put(`/production/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      toast.success('Production record updated successfully');
      setIsModalOpen(false);
      setEditingProduction(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update production record');
    },
  });

  // Delete production mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/production/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productions'] });
      toast.success('Production record deleted successfully');
      setDeletingProduction(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete production record');
    },
  });

  const onSubmit = (data: ProductionForm) => {
    if (editingProduction) {
      updateMutation.mutate({ id: editingProduction.id, data });
    } else {
      if (showCompanyColumn && !selectedTenantId) {
        toast.error('Please select a company');
        return;
      }
      createMutation.mutate({ ...data, tenantId: showCompanyColumn ? selectedTenantId : undefined });
    }
  };

  const handleEdit = (production: Production) => {
    setEditingProduction(production);
    reset({
      machineId: production.machineId,
      beamId: production.beamId,
      employeeId: production.employeeId,
      meters: production.meters,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (production: Production) => {
    setDeletingProduction(production);
  };

  const confirmDelete = () => {
    if (deletingProduction) {
      deleteMutation.mutate(deletingProduction.id);
    }
  };

  const openModal = () => {
    setEditingProduction(null);
    reset({
      machineId: 0,
      beamId: 0,
      qualityId: 0,
      employeeId: 0,
      meters: 0,
      errorRate: 5,
    });
    setSelectedTenantId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduction(null);
    setSelectedTenantId(null);
    reset();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('common.error')}: {t('production.loadError')}</p>
      </div>
    );
  }

  // Calculate statistics
  const totalProductions = productions.length;
  const completedProductions = productions.filter((p: Production) => p.status === 'completed').length;
  const activeProductions = productions.filter((p: Production) => p.status === 'active').length;
  const totalMetersProduced = productions.reduce((sum: number, p: Production) => sum + (p.metersProduced || 0), 0);

  return (
    <div className="space-y-6">
      {/* Floating Add Production button */}
      <div className="fixed top-6 right-6 z-[1002]">
        <button
          type="button"
          onClick={openModal}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
          title="Add Production"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Factory className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Productions</h3>
              <p className="text-2xl font-bold text-blue-600">{totalProductions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Completed</h3>
              <p className="text-2xl font-bold text-green-600">{completedProductions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Active</h3>
              <p className="text-2xl font-bold text-orange-600">{activeProductions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-purple-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Meters</h3>
              <p className="text-2xl font-bold text-purple-600">{totalMetersProduced.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Productions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {productions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('production.noRecords')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {productions.map((production: Production) => (
              <li key={production.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Factory className="h-5 w-5 text-orange-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Machine #{production.machine.number} • Beam #{production.beam.number}
                          </div>
                          <div className="text-sm text-gray-500">
                            Quality: {production.quality.name} • Employee: {production.employee.user.name}
                          </div>
                          {showCompanyColumn && (
                            <div className="text-sm text-gray-500">
                              Company: <CompanyCell tenantId={production.tenantId} />
                            </div>
                          )}
                          <div className="text-sm text-gray-500">
                            Target: {production.meters}m • Produced: {production.metersProduced || 0}m • Error Rate: {production.errorRate}%
                          </div>
                          <div className="text-sm text-gray-500">
                            Created {new Date(production.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        
                        {/* Status Badge and Progress */}
                        <div className="ml-6 text-right">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                            production.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : production.status === 'active'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {production.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {production.status === 'active' && <Clock className="w-3 h-3 mr-1" />}
                            {production.status.toUpperCase()}
                          </span>
                          {production.status === 'active' && production.meters > 0 && (
                            <div className="mt-2">
                              <div className="text-xs text-gray-500 mb-1">
                                Progress: {((production.metersProduced / production.meters) * 100).toFixed(1)}%
                              </div>
                              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-orange-500 transition-all"
                                  style={{ width: `${Math.min((production.metersProduced / production.meters) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(production)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(production)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add/Edit Production Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingProduction ? t('production.editProduction') : t('production.addNewProduction')}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {showCompanyColumn && !editingProduction && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                    <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                  </div>
                )}
                {/* Production Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('production.machine')}</label>
                    <select
                      {...register('machineId', { required: t('production.machineRequired') })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>{t('production.selectMachine')}</option>
                      {machines.map((machine: Machine) => (
                        <option key={machine.id} value={machine.id}>
                          {machine.number}
                        </option>
                      ))}
                    </select>
                    {errors.machineId && (
                      <p className="mt-1 text-sm text-red-600">{t('production.machineRequired')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('production.beam')}</label>
                    <select
                      {...register('beamId', { required: t('production.beamRequired') })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>{t('production.selectBeam')}</option>
                      {beams.map((beam: Beam) => (
                        <option key={beam.id} value={beam.id}>
                          {beam.number} {beam.quality && `(${beam.quality.name})`}
                        </option>
                      ))}
                    </select>
                    {errors.beamId && (
                      <p className="mt-1 text-sm text-red-600">{t('production.beamRequired')}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quality</label>
                    <select
                      {...register('qualityId', { required: 'Quality is required' })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>Select Quality</option>
                      {qualities.map((quality: Quality) => (
                        <option key={quality.id} value={quality.id}>
                          {quality.name}
                        </option>
                      ))}
                    </select>
                    {errors.qualityId && (
                      <p className="mt-1 text-sm text-red-600">Quality is required</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('production.employee')}</label>
                    <select
                      {...register('employeeId', { required: t('production.employeeRequired') })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>{t('production.selectEmployee')}</option>
                      {employees.map((employee: Employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.user.name}
                        </option>
                      ))}
                    </select>
                    {errors.employeeId && (
                      <p className="mt-1 text-sm text-red-600">{t('production.employeeRequired')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('production.metersProduced')}</label>
                    <input
                      {...register('meters', { 
                        required: t('production.metersRequired'),
                        min: { value: 0.01, message: t('production.metersMin') }
                      })}
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      autoFocus
                    />
                    {errors.meters && (
                      <p className="mt-1 text-sm text-red-600">{errors.meters.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Error Rate (%)</label>
                    <input
                      {...register('errorRate')}
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      defaultValue={5}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="5"
                    />
                    <p className="mt-1 text-xs text-gray-500">Default: 5% (Leave empty for default)</p>
                  </div>
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Settings className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>{t('common.notes')}:</strong> {t('production.qualityNote')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    {t('production.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending ? t('production.saving') : t('production.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingProduction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Production Record</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this production record? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingProduction(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Production;